import React, { useState, useRef, useEffect } from 'react';
import { MdCloudUpload, MdDownload } from 'react-icons/md';
import { RxCross2 } from 'react-icons/rx';
import { FaFile, FaFilePdf, FaFileWord, FaFileExcel, FaFileVideo, FaFileImage } from 'react-icons/fa';
import Loader from './loader';
import { deleteMedia } from '../../services/upload-service';

const MultipleAttachmentInput = ({
    handleFileUpload,
    uploadedFiles = [],
    handleRemoveFile,
    isUploading,
    accountId,
    label
}) => {
    const fileInputRef = useRef(null);
    const filesContainerRef = useRef(null);
    const [errorMessage, setErrorMessage] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    // Scroll to end when new files are added
    useEffect(() => {
        if (filesContainerRef.current) {
            filesContainerRef.current.scrollTo({
                left: filesContainerRef.current.scrollWidth,
                behavior: 'smooth'
            });
        }
    }, [uploadedFiles.length]);

    const triggerFileInput = () => {
        fileInputRef.current.click();
    };

    const getFileIcon = (fileType) => {
        if (fileType?.includes('pdf')) return <FaFilePdf className="text-red-500 text-3xl" />;
        if (fileType?.includes('word') || fileType?.includes('msword')) return <FaFileWord className="text-blue-500 text-3xl" />;
        if (fileType?.includes('excel') || fileType?.includes('spreadsheetml')) return <FaFileExcel className="text-green-500 text-3xl" />;
        if (fileType?.startsWith('video/')) return <FaFileVideo className="text-purple-500 text-3xl" />;
        if (fileType?.startsWith('image/')) return <FaFileImage className="text-blue-400 text-3xl" />;
        return <FaFile className="text-gray-500 text-3xl" />;
    };

    const handleDownload = async (file) => {
        if (!file.signedUrl) {
            alert('No file available for download.');
            return;
        }

        try {
            const response = await fetch(file.signedUrl);
            if (!response.ok) throw new Error('Failed to fetch file.');

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.href = url;
            link.download = file.fileName || 'attachment';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Download failed:', error);
            alert('Failed to download the file. The URL may be invalid or expired.');
        }
    };

    const handleDelete = async (file) => {
        if (!file.mediaId || !accountId) {
            setErrorMessage('No media or account information available for deletion.');
            return;
        }

        try {
            setIsDeleting(true);
            setErrorMessage(null);
            const res = await deleteMedia(accountId, file.mediaId);
            console.log(res)
            if (res.status === 204) {
                handleRemoveFile(file);
            } else {
                setErrorMessage(res.data?.message || 'Failed to delete media.');
            }
        } catch (error) {
            console.error('Error deleting media:', error);
            setErrorMessage('An error occurred while deleting the media.');
        } finally {
            setIsDeleting(false);
        }
    };

    const handleFileChange = (e) => {
        const files = Array.from(e.target.files);
        if (files.length > 0) {
            handleFileUpload(files);
        }
        // Reset the input to allow selecting the same files again
        e.target.value = null;
    };

    return (
        <div className="mb-1 flex flex-col sm:col-span-2 col-span-1 items-start w-full justify-between">
            <p className="block text-sm font-medium text-gray-700 mb-2">
                {label || 'Attach Files'}
            </p>
            {errorMessage && (
                <p className="text-red-600 text-sm mb-2">{errorMessage}</p>
            )}
            <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept="image/*,video/*,application/pdf,.doc,.docx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                className="hidden"
                multiple
                disabled={isUploading || isDeleting}
            />
            <div className={`relative w-full flex justify-center items-center border-2 border-dashed border-blue-400 h-32 rounded-lg bg-bg ${(isUploading || isDeleting) ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                <div className="absolute inset-0 flex items-center">
                    <div className="flex w-full h-full">
                        {/* Left side - Files or empty state */}
                        <div
                            ref={filesContainerRef}
                            className="flex-1 flex items-center overflow-x-auto overflow-y-hidden p-2"
                        >
                            {uploadedFiles.length === 0 ? (
                                <div className="w-full text-center text-gray-500">
                                    No file uploaded yet
                                </div>
                            ) : (
                                <div className="flex items-center space-x-2">
                                    {uploadedFiles.map((file, index) => {
                                        const isImage = file.fileType?.startsWith('image/');
                                        const previewUrl = file.imagePreview || file.signedThumbUrl || file.signedUrl;

                                        return (
                                            <div
                                                key={index}
                                                className="relative flex-shrink-0 w-24 h-24 bg-white rounded-lg border border-gray-200 flex flex-col items-center justify-center px-2 hover:shadow-md transition-shadow"
                                            >
                                                {isImage && previewUrl ? (
                                                    <img
                                                        src={previewUrl}
                                                        alt="File preview"
                                                        className="h-16 w-full object-contain"
                                                    />
                                                ) : (
                                                    <div className="flex flex-col items-center">
                                                        {getFileIcon(file.fileType)}
                                                    </div>
                                                )}
                                                <p className="text-xs text-gray-500 truncate w-full text-center mt-1">
                                                    {file.fileName || `File ${index + 1}`}
                                                </p>

                                                <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-20 flex items-center justify-center space-x-2 opacity-0 hover:opacity-100 transition-opacity rounded-lg">
                                                    {file.signedUrl && (
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleDownload(file);
                                                            }}
                                                            className="bg-white rounded-full p-1 shadow-md hover:bg-gray-100"
                                                            title="Download"
                                                        >
                                                            <MdDownload className="text-blue-500 text-lg" />
                                                        </button>
                                                    )}
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleDelete(file);
                                                        }}
                                                        className="bg-white rounded-full p-1 shadow-md hover:bg-gray-100"
                                                        title="Remove"
                                                    >
                                                        <RxCross2 className="text-red-500 text-lg" />
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {/* Right side - Always visible upload button */}
                        <div
                            className="flex w-28 px-4 h-full bg-gray-100 border border-gray-200 rounded-r-lg text-center p-1 flex-col justify-center items-center gap-1"
                            onClick={(e) => {
                                e.stopPropagation();
                                if (!isUploading && !isDeleting) {
                                    triggerFileInput();
                                }
                            }}
                        >
                            {
                                (isUploading || isDeleting) ?
                                    <Loader className='text-xs' loaderStyle="h-7 w-7" message={isUploading ? 'Uploading...' : 'Deleting...'} />
                                    :
                                    <div className='w-full flex flex-col items-center'>
                                        <MdCloudUpload className="text-3xl text-blue-500" />
                                        <p className="text-gray-500 text-xs">Upload</p>
                                    </div>
                            }

                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MultipleAttachmentInput;